// i18n — język warstwy wyświetlania (MONIT 2026-10-02_0526, T Martina 02.10.2026).
//
// JEDEN moduł języka + JEDEN słownik PL (i18n.pl.js) + JEDEN słownik DE (i18n.de.js).
// Bez biblioteki: potrzeby to płaskie klucze, wstawki {x}, liczba mnoga i jeden
// przełącznik. Każda zewnętrzna biblioteka (i18next, react-intl) niosłaby więcej
// kodu niż ten plik i wymagałaby dostawcy Reacta, którego nie mamy poza Reactem
// (toastBus, services, shared).
//
// Zasady:
//   - słowniki w bundlu (zero fetch, działa offline),
//   - wybór języka czytany PRZY IMPORCIE (przed pierwszym renderem; wzorzec useTheme),
//   - dostępny poza Reactem: t() czyta bieżący język w chwili wywołania — NIGDY nie
//     zapisuj wyniku t() do stałej modułu (zamrozi język z chwili importu),
//   - brak klucza DE = tekst PL; brak klucza PL = sam klucz (test i18n.test.js pilnuje,
//     żeby każdy klucz użyty w src istniał w PL, więc do użytkownika to nie dociera),
//   - plik płaski w src/shared (kopiowany do backendu przez prep; node-safe: zero DOM
//     przy imporcie, DOM dotykany tylko w try/catch).
//
// Persystencja: localStorage 'fiberops_lang' ('pl' | 'de'), default 'pl' (per urządzenie,
// jak motyw — DN w meldunku).

import { PL } from './i18n.pl.js';
import { DE } from './i18n.de.js';

export const LANGS = Object.freeze(['pl', 'de']);
export const DEFAULT_LANG = 'pl';
const LS_KEY = 'fiberops_lang';
const DICTS = Object.freeze({ pl: PL, de: DE });
const listeners = new Set();

function normalize(v) {
  return v === 'de' ? 'de' : 'pl';
}

function readLang() {
  try {
    return normalize(localStorage.getItem(LS_KEY));
  } catch {
    return DEFAULT_LANG;
  }
}

let current = readLang();

/** <html lang> + ewentualny atrybut dla CSS; bez DOM (node) nic nie robi. */
function applyDocumentLang(lang) {
  try {
    document.documentElement.setAttribute('lang', lang);
  } catch { /* node / testy */ }
}

applyDocumentLang(current);

export function getLang() {
  return current;
}

export function setLang(next) {
  const lang = normalize(next);
  if (lang === current) return;
  current = lang;
  try { localStorage.setItem(LS_KEY, lang); } catch { /* private mode */ }
  applyDocumentLang(lang);
  for (const fn of listeners) { try { fn(lang); } catch { /* słuchacz nie wywraca nadawcy */ } }
}

export function toggleLang() {
  setLang(current === 'pl' ? 'de' : 'pl');
}

export function subscribeLang(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// ─── Wstawki ────────────────────────────────────────────────────────────────

/** `fmt('Zdjęć: {n} z {max}', { n: 2, max: 5 })` → 'Zdjęć: 2 z 5'. Brak wartości = puste. */
export function fmt(str, params) {
  if (typeof str !== 'string') return str;
  if (!params) return str;
  return str.replace(/\{(\w+)\}/g, (m, k) => (params[k] === undefined || params[k] === null ? '' : String(params[k])));
}

function lookup(lang, key) {
  const d = DICTS[lang];
  const v = d ? d[key] : undefined;
  return v === undefined || v === null || v === '' ? undefined : v;
}

/**
 * Tekst w bieżącym (albo wskazanym) języku. DE bez klucza → PL. PL bez klucza → klucz.
 * @param {string} key
 * @param {Object} [params] - wstawki {x}; `params.lang` wymusza język (np. do testów)
 */
export function t(key, params) {
  const lang = params && params.lang ? normalize(params.lang) : current;
  let v = lookup(lang, key);
  if (v === undefined && lang !== 'pl') v = lookup('pl', key);
  if (v === undefined) return key;
  if (typeof v !== 'string') v = pluralPick(lang, v, params && params.n);
  return fmt(v, params);
}

// ─── Liczba mnoga ───────────────────────────────────────────────────────────

/** PL: one (1) / few (2-4, poza 12-14) / many (reszta). DE: one (1) / other. */
export function pluralCategory(lang, n) {
  const a = Math.abs(Number(n) || 0);
  if (normalize(lang) === 'de') return a === 1 ? 'one' : 'other';
  if (a === 1) return 'one';
  const r10 = a % 10;
  const r100 = a % 100;
  if (r10 >= 2 && r10 <= 4 && !(r100 >= 12 && r100 <= 14)) return 'few';
  return 'many';
}

function pluralPick(lang, forms, n) {
  if (!forms || typeof forms !== 'object') return String(forms ?? '');
  const cat = pluralCategory(lang, n);
  return forms[cat] ?? forms.other ?? forms.many ?? forms.few ?? forms.one ?? '';
}

/**
 * Liczba mnoga: klucz wskazuje obiekt form { one, few, many } (PL) / { one, other } (DE).
 * Wstawka {n} dostępna w każdej formie. `tp('queue.photos', 3)` → 'zdjęcia'.
 */
export function tp(key, n, params) {
  return t(key, { ...(params || {}), n });
}

/** Czy klucz istnieje w słowniku PL (źródle kluczy). */
export function hasKey(key) {
  return Object.prototype.hasOwnProperty.call(PL, key);
}

/** Krótki kod języka do przełącznika: 'PL' / 'DE'. */
export function langCode(lang = current) {
  return normalize(lang).toUpperCase();
}

/** Locale dla Intl / toLocale*: 'pl-PL' | 'de-DE'. */
export function langLocale(lang = current) {
  return normalize(lang) === 'de' ? 'de-DE' : 'pl-PL';
}

// Eksport do testów (bez Reacta).
export const __internal = {
  readLang, normalize, LS_KEY, DICTS,
  get current() { return current; },
  /** Tylko testy: ustaw język bez localStorage i bez słuchaczy. */
  force(lang) { current = normalize(lang); },
};
